import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import SearchOutlined from "@ant-design/icons/SearchOutlined";
import { ArrowLeft, Search } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import React, { useContext, useEffect } from "react";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useInput from "utils/hooks/useInput";
import Input from "common/Inputs/Input.js";
import { useHistory } from "react-router-dom";
import { HEADER_ICON_SIZE } from "@shared/common/Themes/constants";
import styled, { useTheme } from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface MobileSearchProps {
  searchOpen: boolean;
  setSearchOpen: (searchOpen: boolean) => void;
}

const MobileSearch = ({ searchOpen, setSearchOpen }: MobileSearchProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { search, setSearchValue } = useContext(PageWrapperContext);
  const { setValue, bind, ref } = useInput("", setSearchValue, null);
  const history = useHistory();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  useEffect(() => {
    if (searchOpen) {
      ref?.current?.focus();
    }
  }, [searchOpen]);

  const clearSearch = () => {
    setSearchOpen(false);
    setSearchValue("");
    setValue("");
  };

  useEffect(() => {
    const unblock = history.block((nextLocation) => {
      const closeSearch =
        searchOpen && !nextLocation.pathname.startsWith(location.pathname);
      if (closeSearch) {
        clearSearch();
      }
    });

    return () => {
      unblock();
    };
  }, [searchOpen]);

  if (!search.show) return null;

  return (
    <>
      {!searchOpen ? (
        <LookIcon
          classic={
            <SearchOutlined
              style={{
                fontSize: HEADER_ICON_SIZE,
                marginLeft: isBottomNavigator ? 4 : 12,
              }}
              onClick={() => setSearchOpen(!searchOpen)}
            />
          }
          newLook={
            <Search
              size={HEADER_ICON_SIZE}
              strokeWidth={1.75}
              style={{
                marginLeft: isBottomNavigator ? 4 : 12,
                cursor: "pointer",
              }}
              onClick={() => setSearchOpen(!searchOpen)}
            />
          }
        />
      ) : (
        <>
          <LookIcon
            classic={
              <ArrowLeftOutlined
                style={{ cursor: "pointer", marginRight: 8 }}
                onClick={clearSearch}
              />
            }
            newLook={
              <ArrowLeft
                size={18}
                strokeWidth={1.75}
                style={{ cursor: "pointer", marginRight: 8, flex: "none" }}
                onClick={clearSearch}
              />
            }
          />
          <StyledInput
            ref={ref}
            variant="borderless"
            placeholder={t("labels.search_placeholder")}
            $bgColor={theme.colors.layoutBgColor}
            {...bind}
          />
        </>
      )}
    </>
  );
};

export default MobileSearch;

const StyledInput = styled(Input)`
  width: 100%;
  max-width: 100%;
  flex: 1;
`;
